import type {
  MerkletreeScanUpdateEvent,
  POIProofProgressEvent,
  RailgunBalancesEvent
} from '@railgun-community/shared-models'
import type { BatchListUpdateEvent } from '@railgun-community/wallet'

export interface RailgunListenerEvents {
  balanceUpdate: RailgunBalancesEvent
  poiProofProgress: POIProofProgressEvent
  utxoScanUpdate: MerkletreeScanUpdateEvent
  txidScanUpdate: MerkletreeScanUpdateEvent
  batchListUpdate: BatchListUpdateEvent
}

export type RailgunEventName = keyof RailgunListenerEvents

export type RailgunListener<K extends RailgunEventName> = (event: RailgunListenerEvents[K]) => void

export interface RailgunEmitter {
  on<K extends RailgunEventName>(event: K, listener: RailgunListener<K>): void
  off<K extends RailgunEventName>(event: K, listener: RailgunListener<K>): void
}

type ListenerMap = {
  [K in RailgunEventName]?: Set<RailgunListener<K>>
}

const listeners: ListenerMap = {}

function on<K extends RailgunEventName>(event: K, listener: RailgunListener<K>): void {
  const existing = listeners[event] as Set<RailgunListener<K>> | undefined
  const set: Set<RailgunListener<K>> = existing ?? new Set<RailgunListener<K>>()

  if (!existing) {
    listeners[event] = set as ListenerMap[K]
  }

  set.add(listener)
}

function off<K extends RailgunEventName>(event: K, listener: RailgunListener<K>): void {
  const set = listeners[event] as Set<RailgunListener<K>> | undefined
  if (!set) return
  set.delete(listener)
  if (set.size === 0) {
    delete listeners[event]
  }
}

export const railgunEmitter: RailgunEmitter = {
  on,
  off
}

export function emitRailgunEvent<K extends RailgunEventName>(
  event: K,
  payload: RailgunListenerEvents[K]
): void {
  const set = listeners[event] as Set<RailgunListener<K>> | undefined
  if (!set || set.size === 0) {
    return
  }

  // Copy to array to avoid issues if listeners mutate subscription during emit.
  for (const listener of Array.from(set)) {
    try {
      listener(payload)
    } catch (err) {
      // Swallow listener errors so one bad consumer doesn't break others.
      // eslint-disable-next-line no-console
      console.error('[railgunEmitter] listener error for event', event, err)
    }
  }
}
